import { matchPath } from 'react-router-dom';
import { getPractitionerConfig } from '../../features/practitioner/config';
import { isGestionPsy } from '../auth/hasRoles';
import { routes } from '../router/routes';
import { breadcrumbLabels as L } from './breadcrumbLabels';

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

const specialistConfig = getPractitionerConfig('specialist');

const SPECIALISTS_PARENT: BreadcrumbItem = {
  label: L.specialistsParent,
  path: specialistConfig.routes.list,
};

function practitionerListCrumbs(kind: 'specialist' | 'socialWorker'): BreadcrumbItem[] {
  const config = getPractitionerConfig(kind);
  const listLabel =
    kind === 'specialist' ? L.psychologistsList : L.socialWorkersList;

  return [SPECIALISTS_PARENT, { label: listLabel, path: config.routes.list }];
}

function adminRefListCrumbs(label: string, listPath: string): BreadcrumbItem[] {
  return [
    { label: L.adminOptions },
    { label, path: listPath },
  ];
}

function adminRefFormCrumbs(
  label: string,
  listPath: string,
  formLabel: string,
): BreadcrumbItem[] {
  return [...adminRefListCrumbs(label, listPath), { label: formLabel }];
}

function markLastAsCurrent(crumbs: BreadcrumbItem[]): BreadcrumbItem[] {
  return crumbs.map((crumb, index) =>
    index === crumbs.length - 1 ? { label: crumb.label } : crumb,
  );
}

type CrumbRule = {
  path: string;
  resolve: () => BreadcrumbItem[];
};

const crumbRules: CrumbRule[] = [
  {
    path: routes.addSpecialistList,
    resolve: () => [
      ...practitionerListCrumbs('specialist'),
      { label: L.addSpecialist },
    ],
  },
  {
    path: routes.editSpecialistList,
    resolve: () => [
      ...practitionerListCrumbs('specialist'),
      { label: L.editSpecialist },
    ],
  },
  {
    path: routes.showSpecialistList,
    resolve: () => [
      ...practitionerListCrumbs('specialist'),
      { label: L.showSpecialist },
    ],
  },
  {
    path: routes.convertSpecialist,
    resolve: () => [
      ...practitionerListCrumbs('specialist'),
      { label: L.convertSpecialist },
    ],
  },
  {
    path: routes.specialistList,
    resolve: () => practitionerListCrumbs('specialist'),
  },
  {
    path: routes.addSocialWorkerList,
    resolve: () => [
      ...practitionerListCrumbs('socialWorker'),
      { label: L.addSocialWorker },
    ],
  },
  {
    path: routes.editSocialWorkerList,
    resolve: () => [
      ...practitionerListCrumbs('socialWorker'),
      { label: L.editSocialWorker },
    ],
  },
  {
    path: routes.showSocialWorkerlist,
    resolve: () => [
      ...practitionerListCrumbs('socialWorker'),
      { label: L.showSocialWorker },
    ],
  },
  {
    path: routes.socialWorkerList,
    resolve: () => practitionerListCrumbs('socialWorker'),
  },
  { path: routes.dashboard, resolve: () => [{ label: L.dashboard }] },
  {
    path: routes.newIntervention,
    resolve: () => [
      { label: L.interventions, path: routes.interventions },
      { label: L.newIntervention },
    ],
  },
  {
    path: routes.editIntervention,
    resolve: () => [
      { label: L.interventions, path: routes.interventions },
      { label: L.manageIntervention },
    ],
  },
  { path: routes.interventions, resolve: () => [{ label: L.interventions }] },
  { path: routes.specialistsMap, resolve: () => [{ label: L.specialistsMap }] },
  { path: routes.restore, resolve: () => [{ label: L.restore }] },
  {
    path: routes.editProspectList,
    resolve: () => [
      { label: L.prospects, path: routes.prospects },
      { label: L.editProspect },
    ],
  },
  {
    path: routes.showProspectList,
    resolve: () => [
      { label: L.prospects, path: routes.prospects },
      { label: L.showProspect },
    ],
  },
  { path: routes.prospects, resolve: () => [{ label: L.prospects }] },
  {
    path: routes.convertProspect,
    resolve: () => [
      { label: L.prospects, path: routes.prospects },
      { label: L.convertProspect },
    ],
  },
  {
    path: routes.editOfficeSpecialist,
    resolve: () => [
      { label: L.prospects, path: routes.prospects },
      { label: L.editOfficeSpecialist },
    ],
  },
  {
    path: routes.showOfficeSpecialist,
    resolve: () => [
      { label: L.prospects, path: routes.prospects },
      { label: L.showOfficeSpecialist },
    ],
  },
  {
    path: routes.newCompany,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.companies, routes.company, L.addCompany)
        : [],
  },
  {
    path: routes.editCompany,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.companies, routes.company, L.editCompany)
        : [],
  },
  {
    path: routes.company,
    resolve: () => (isGestionPsy() ? adminRefListCrumbs(L.companies, routes.company) : []),
  },
  {
    path: routes.newSpecialities,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.specialities, routes.specialities, L.addSpeciality)
        : [],
  },
  {
    path: routes.editSpecialities,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.specialities, routes.specialities, L.editSpeciality)
        : [],
  },
  {
    path: routes.specialities,
    resolve: () =>
      isGestionPsy() ? adminRefListCrumbs(L.specialities, routes.specialities) : [],
  },
  {
    path: routes.newExperiences,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.experiences, routes.experiences, L.addExperience)
        : [],
  },
  {
    path: routes.editExperiences,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.experiences, routes.experiences, L.editExperience)
        : [],
  },
  {
    path: routes.experiences,
    resolve: () =>
      isGestionPsy() ? adminRefListCrumbs(L.experiences, routes.experiences) : [],
  },
  {
    path: routes.newExpertises,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.expertises, routes.expertises, L.addExpertise)
        : [],
  },
  {
    path: routes.editExpertises,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.expertises, routes.expertises, L.editExpertise)
        : [],
  },
  {
    path: routes.expertises,
    resolve: () =>
      isGestionPsy() ? adminRefListCrumbs(L.expertises, routes.expertises) : [],
  },
  {
    path: routes.newLanguages,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.languages, routes.languages, L.addLanguage)
        : [],
  },
  {
    path: routes.editLanguages,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.languages, routes.languages, L.editLanguage)
        : [],
  },
  {
    path: routes.languages,
    resolve: () => (isGestionPsy() ? adminRefListCrumbs(L.languages, routes.languages) : []),
  },
  {
    path: routes.newAdministratifStatus,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(
            L.administratifStatus,
            routes.administratifStatus,
            L.addAdministratifStatus,
          )
        : [],
  },
  {
    path: routes.editAdministratifStatus,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(
            L.administratifStatus,
            routes.administratifStatus,
            L.editAdministratifStatus,
          )
        : [],
  },
  {
    path: routes.administratifStatus,
    resolve: () =>
      isGestionPsy()
        ? adminRefListCrumbs(L.administratifStatus, routes.administratifStatus)
        : [],
  },
  {
    path: routes.newCertifications,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.certifications, routes.certifications, L.addCertification)
        : [],
  },
  {
    path: routes.editCertifications,
    resolve: () =>
      isGestionPsy()
        ? adminRefFormCrumbs(L.certifications, routes.certifications, L.editCertification)
        : [],
  },
  {
    path: routes.certifications,
    resolve: () =>
      isGestionPsy() ? adminRefListCrumbs(L.certifications, routes.certifications) : [],
  },
  {
    path: routes.editUserAccount,
    resolve: () => [
      { label: L.users, path: routes.userAccount },
      { label: L.editUser },
    ],
  },
  { path: routes.userAccount, resolve: () => [{ label: L.users }] },
  { path: routes.editAdminAccount, resolve: () => [{ label: L.profile }] },
  { path: routes.journalisationConnexions, resolve: () => [{ label: L.journalisation }] },
];

export function getAppBreadcrumbs(pathname: string): BreadcrumbItem[] {
  for (const rule of crumbRules) {
    const match = matchPath({ path: rule.path, end: true }, pathname);
    if (!match) continue;

    const crumbs = rule.resolve();
    if (crumbs.length === 0) continue;

    return markLastAsCurrent(crumbs);
  }

  return [];
}
